import React, { useEffect, useMemo, useState } from "react";
import axiosClient from "../api/axiosClient";
import AdminProductForm from "./AdminProductForm";
import "./AdminProducts.css";

const AdminProducts = () => {
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);

  const [editingProduct, setEditingProduct] = useState(null);

  const [searchText, setSearchText] = useState("");

  const [selectedCategory, setSelectedCategory] = useState("All");

  const loadProducts = async () => {
    try {
      setLoading(true);

      const response = await axiosClient.get("/admin/products");

      setProducts(response.data);

      setError("");
    } catch (error) {
      console.error("Error loading products:", error);

      setError("Unable to load products. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const handleAddProduct = () => {
    setEditingProduct(null);

    setShowForm(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);

    setShowForm(true);
  };

  const handleDeleteProduct = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this product?",
    );

    if (!confirmDelete) {
      return;
    }

    try {
      await axiosClient.delete(`/admin/products/${id}`);

      await loadProducts();
    } catch (error) {
      console.error("Error deleting product:", error);

      alert("Unable to delete product.");
    }
  };

  const handleFormSuccess = async () => {
    setShowForm(false);

    setEditingProduct(null);

    await loadProducts();
  };

  const handleFormCancel = () => {
    setShowForm(false);

    setEditingProduct(null);
  };

  const categories = useMemo(() => {
    const categorySet = new Set();

    products.forEach((product) => {
      if (product.category) {
        categorySet.add(product.category);
      }
    });

    return ["All", ...Array.from(categorySet)];
  }, [products]);

  const filteredProducts = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return products.filter((product) => {
      const matchesSearch =
        search === "" ||
        product.name?.toLowerCase().includes(search) ||
        product.category?.toLowerCase().includes(search) ||
        product.description?.toLowerCase().includes(search);

      const matchesCategory =
        selectedCategory === "All" || product.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [products, searchText, selectedCategory]);

  if (loading) {
    return (
      <div className="admin-products-loading">
        <div className="admin-loading-spinner"></div>

        <p>Loading your products...</p>
      </div>
    );
  }

  return (
    <div className="admin-products">
      {/* PAGE INTRO */}

      <div className="admin-products-heading">
        <div>
          <p className="admin-section-eyebrow">CATALOGUE</p>

          <h2>Products</h2>

          <p className="admin-products-subtitle">
            Manage your jewellery collection, pricing and inventory.
          </p>
        </div>

        <button
          type="button"
          className="admin-add-product-button"
          onClick={handleAddProduct}
        >
          <span>+</span>
          Add Product
        </button>
      </div>

      {/* SUMMARY */}

      <div className="admin-product-summary">
        <div className="admin-summary-card">
          <span className="admin-summary-label">Total Products</span>

          <strong>{products.length}</strong>
        </div>

        <div className="admin-summary-card">
          <span className="admin-summary-label">In Stock</span>

          <strong>
            {products.filter((product) => Number(product.stock) > 0).length}
          </strong>
        </div>

        <div className="admin-summary-card">
          <span className="admin-summary-label">Out of Stock</span>

          <strong>
            {products.filter((product) => Number(product.stock) <= 0).length}
          </strong>
        </div>
      </div>

      {/* ERROR */}

      {error && <div className="admin-products-error">{error}</div>}

      {/* ADD / EDIT FORM */}

      {showForm && (
        <AdminProductForm
          product={editingProduct}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      {/* SEARCH + FILTER */}

      <div className="admin-products-toolbar">
        <div className="admin-product-search">
          <span>⌕</span>

          <input
            type="text"
            placeholder="Search products..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>

        <div className="admin-product-filter">
          <label htmlFor="adminCategory">Category</label>

          <select
            id="adminCategory"
            value={selectedCategory}
            onChange={(event) => setSelectedCategory(event.target.value)}
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* RESULT COUNT */}

      <div className="admin-product-result">
        Showing <strong>{filteredProducts.length}</strong> of{" "}
        <strong>{products.length}</strong> products
      </div>

      {/* PRODUCT GRID */}

      {filteredProducts.length === 0 ? (
        <div className="admin-no-products">
          <div className="admin-no-products-icon">◇</div>

          <h3>No products found</h3>

          <p>Try another search or category.</p>
        </div>
      ) : (
        <div className="admin-product-grid">
          {filteredProducts.map((product) => {
            const price = Number(product.price || 0);

            const salePrice =
              product.salePrice !== null && product.salePrice !== undefined
                ? Number(product.salePrice)
                : null;

            const hasSale = salePrice !== null && salePrice < price;

            const stock = Number(product.stock || 0);

            return (
              <article className="admin-product-card" key={product.id}>
                {/* IMAGE */}

                <div className="admin-product-image-wrapper">
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="admin-product-image"
                  />

                  {stock <= 0 && (
                    <span className="admin-stock-badge out">OUT OF STOCK</span>
                  )}

                  {stock > 0 && stock <= 3 && (
                    <span className="admin-stock-badge low">LOW STOCK</span>
                  )}
                </div>

                {/* CONTENT */}

                <div className="admin-product-card-content">
                  <p className="admin-product-category">{product.category}</p>

                  <h3>{product.name}</h3>

                  <div className="admin-product-prices">
                    {hasSale ? (
                      <>
                        <span className="admin-original-price">
                          ₹{price.toLocaleString("en-IN")}
                        </span>

                        <strong className="admin-sale-price">
                          ₹{salePrice.toLocaleString("en-IN")}
                        </strong>
                      </>
                    ) : (
                      <strong className="admin-regular-price">
                        ₹{price.toLocaleString("en-IN")}
                      </strong>
                    )}
                  </div>

                  <div className="admin-product-stock">
                    <span>Stock</span>

                    <strong
                      className={
                        stock <= 0
                          ? "stock-out"
                          : stock <= 3
                            ? "stock-low"
                            : "stock-good"
                      }
                    >
                      {stock}
                    </strong>
                  </div>

                  {/* ACTIONS */}

                  <div className="admin-product-actions">
                    <button
                      type="button"
                      className="admin-edit-button"
                      onClick={() => handleEditProduct(product)}
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      className="admin-delete-button"
                      onClick={() => handleDeleteProduct(product.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
